import {
  useCallback,
  useId,
  type CSSProperties,
  type KeyboardEventHandler,
  type MouseEventHandler,
  type ReactNode,
  type RefObject,
} from 'react';

import { cx } from './classNames';

/**
 * Gemeinsame Hülle aller Eingabefelder (`e-input-group`). Die Beschriftung steht
 * **fest über** dem Feld (REQ-24 §2) und wird von einem echten `<label>`
 * zusammen mit dem Feld umschlossen. Damit kann sie den Feldinhalt nicht mehr
 * überlappen — die Fehlerklasse, die REQ-23 §5 noch abfangen musste, entfällt —
 * und ein Klick auf die Beschriftung setzt den Fokus ins Feld.
 *
 * Ohne `label` bleibt allein die Eingabegruppe stehen. So arbeitet die
 * Grid-Filterzeile: Dort liefert die Spaltenüberschrift die Beschriftung, und
 * der Platzhalter ist die richtige Lösung (REQ-24 §3.3).
 */
export interface FieldLabelProps {
  /** Beschriftung über dem Feld. Fehlt sie, wird nur das Feld gerendert. */
  label?: string;
  /** Zusatz in schwächerer Schrift hinter der Beschriftung. */
  hint?: string;
  /** Zusatzklassen der Eingabegruppe, z. B. `e-numeric`, `e-ddl`, `e-date-wrapper`. */
  groupClass?: string;
  focused?: boolean;
  disabled?: boolean;
  id?: string;
  className?: string;
  style?: CSSProperties;
  title?: string;
  /**
   * Äußerstes Element: Anker der Popups und Bezugspunkt der
   * Klick-außerhalb-Erkennung. Mit Beschriftung ist das das `<label>`, ohne sie
   * die Eingabegruppe. Beide beginnen und enden an derselben Kante, die Lage
   * eines Popups ändert sich dadurch nicht.
   */
  elementRef?: RefObject<HTMLElement | null>;
  onClick?: MouseEventHandler<HTMLElement>;
  onKeyDown?: KeyboardEventHandler<HTMLElement>;
  /** Feldinhalt; die `id` gehört auf das Bedienelement. */
  children: (props: { id: string }) => ReactNode;
}

export function FieldLabel(props: FieldLabelProps) {
  const { elementRef } = props;
  const fieldId = useId();

  // Der Ref wird über einen Rückruf gesetzt: Das äußerste Element ist mal ein
  // <label>, mal ein <span>, und ein `RefObject<HTMLElement>` ließe sich an
  // keines von beiden unverändert weiterreichen.
  const attach = useCallback(
    (node: HTMLElement | null) => {
      if (elementRef !== undefined) elementRef.current = node;
    },
    [elementRef],
  );

  const groupClass = cx(
    'e-input-group',
    props.groupClass,
    props.focused === true && 'e-input-focus',
    props.disabled === true && 'e-disabled',
  );
  const content = props.children({ id: fieldId });

  if (props.label === undefined) {
    return (
      <span
        id={props.id}
        ref={attach}
        title={props.title}
        style={props.style}
        className={cx(groupClass, props.className)}
        onClick={props.onClick}
        onKeyDown={props.onKeyDown}
      >
        {content}
      </span>
    );
  }

  return (
    <label
      id={props.id}
      ref={attach}
      title={props.title}
      style={props.style}
      className={cx('iwm-field', props.className)}
      onClick={props.onClick}
      onKeyDown={props.onKeyDown}
    >
      <span className="iwm-field-label">
        {props.label}
        {props.hint === undefined ? null : <span className="iwm-field-hint">{props.hint}</span>}
      </span>
      {/* Ein <label> leitet jeden Klick, der nicht ohnehin ein Bedienelement
          trifft, zusätzlich an das umschlossene Feld weiter. Die Symbole der
          Eingabegruppe (Auswahlpfeil, Kalender, Löschen) sind
          `span[role=button][tabindex=0]` — die Browser zählen sie nicht zu den
          Bedienelementen, sodass der Auswahlpfeil die Liste zweimal umschalten
          würde: einmal selbst, einmal über den weitergereichten Klick auf das
          Eingabefeld. Sie bliebe damit zu. `preventDefault` unterbindet die
          Weiterleitung für alles innerhalb des Feldes; der Klick auf die
          Beschriftung darüber setzt weiterhin den Fokus ins Feld. */}
      <span
        className={groupClass}
        onClick={(event) => {
          event.preventDefault();
        }}
      >
        {content}
      </span>
    </label>
  );
}

export default FieldLabel;
